"use client";
import { useState, useEffect, useRef } from "react";
import PriceHistogram from "./PriceHistogram";

const CATEGORY_ICONS = {
  "Art Supplies":              { emoji: "🎨", bg: "#fefce8", border: "#fde047" },
  "Books":                     { emoji: "📚", bg: "#eff6ff", border: "#93c5fd" },
  "Classroom Basics":          { emoji: "📦", bg: "#f0fdf4", border: "#86efac" },
  "Educational Games":         { emoji: "🎮", bg: "#fdf4ff", border: "#d8b4fe" },
  "Food, Clothing & Hygiene":  { emoji: "🧴", bg: "#fef2f2", border: "#fca5a5" },
  "Instructional Technology":  { emoji: "💻", bg: "#eff6ff", border: "#93c5fd" },
  "STEM":                      { emoji: "🔬", bg: "#f0fdf4", border: "#86efac" },
  "Decor":                     { emoji: "✨", bg: "#fdf4ff", border: "#d8b4fe" },
  "Furniture":                 { emoji: "🪑", bg: "#fff7ed", border: "#fdba74" },
  "Other":                     { emoji: "🎁", bg: "#f8fafc", border: "#e2e8f0" },
};

export default function ShareBucketGrid({ schoolID, filters, onCopyLink, copied, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!schoolID) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    const params = new URLSearchParams();
    if (filters?.itemMinPrice) params.set("itemMinPrice", filters.itemMinPrice);
    if (filters?.itemMaxPrice) params.set("itemMaxPrice", filters.itemMaxPrice);
    if (filters?.categories?.length) params.set("categories", filters.categories.join(","));

    fetch(`/api/wishlist/${schoolID}/summary?${params.toString()}`, { signal: controller.signal })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { if (e.name !== "AbortError") setLoading(false); });

    return () => controller.abort();
  }, [schoolID, filters]);

  if (loading && !data) return (
    <div style={{
      background: "#fff", borderTop: "6px solid #fbbf24",
      borderRadius: 24, display: "flex", alignItems: "center",
      justifyContent: "center", minHeight: 300,
      fontFamily: FREDOKA, fontSize: 16, color: "#94a3b8",
    }}>
      🍎 Loading wishlist...
    </div>
  );

  if (!data) return null;

  const teacherName  = data.teacherName || "This Teacher";
  const schoolName   = data.schoolName  || "";
  const city         = data.city        || "";
  const state        = data.state       || "";
  const grade        = data.grade       || "";
  const wishlistUrl  = data.wishlistUrl || null;
  const totalItems   = data.stats?.totalItems  ?? 0;
  const byCategory   = data.stats?.byCategory  ?? {};
  const histogram    = data.histogram          ?? [];
  const cheapestItems = data.cheapestItems     ?? [];

  const topCategories = Object.entries(byCategory)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const location = [city && state ? `${city}, ${state}` : state || city, grade ? `Grade ${grade}` : ""].filter(Boolean).join(" · ");

  return (
    <div style={{
      background:   "#fff",
      borderRadius: 24,
      overflow:     "hidden",
      fontFamily:   NUNITO,
      borderTop:    "6px solid #fbbf24",
      position:     "relative",
    }}>

      {/* Close button — top right */}
      {onClose && (
        <button onClick={onClose} style={{
          position: "absolute", top: 14, right: 14, zIndex: 10,
          width: 30, height: 30, borderRadius: "50%",
          background: "#fef2f2", border: "2px solid #fca5a5",
          color: "#dc2626", fontSize: 16, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
        }}>×</button>
      )}

      {/* Hero header */}
      <div style={{ background: "#fff", borderBottom: "3px solid #e2e8f0", padding: "20px 44px 16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

          {/* Pencil avatar */}
          <div style={{
            width: 52, height: 52, borderRadius: "50%", flexShrink: 0,
            background: "#fef9c3", border: "3px solid #fde047",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 26,
          }}>
            ✏️
          </div>

          {/* Name + school */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FREDOKA, fontSize: 20, color: "#1e293b", lineHeight: 1.1 }}>
              {teacherName}
            </div>
            {schoolName && (
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginTop: 2 }}>
                {schoolName}
              </div>
            )}
            {location && (
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, marginTop: 1 }}>
                {location}
              </div>
            )}
          </div>
        </div>

        {/* Headline */}
        {/* <div style={{
          marginTop: 10, fontFamily: FREDOKA, fontSize: 13,
          color: "#475569", lineHeight: 1.4,
        }}>
          {teacherName.split(" ")[0]}'s students need supplies —<br />can you help clear the list? 🍎
        </div> */}
      </div>

      <div style={{ fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, color: "#94a3b8", padding: "10px 14px 4px" }}>
        Cheapest ways to help
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "0 14px 8px" }}>
        {cheapestItems.map((item, i) => (
          <div key={i} style={{
            borderRadius: 12, padding: "10px 12px",
            border: "2px solid #fdba74", background: "#fff7ed",
            display: "flex", flexDirection: "column", justifyContent: "space-between",
            minHeight: 56,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 800, color: "#1e293b",
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              lineHeight: 1.3,
            }}>
              {item.product_title}
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#f97316", marginTop: 4 }}>
              ${item.price}
            </div>
          </div>
        ))}
      </div>

      {/* Histogram */}
      {/* <div style={{ fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, color: "#94a3b8", padding: "2px 14px 4px" }}>
        Requests by price
      </div> */}
      <div style={{ margin: "0 14px 12px" }}>
        <PriceHistogram
          histogram={histogram}
          itemMinPrice={filters?.itemMinPrice}
          itemMaxPrice={filters?.itemMaxPrice}
          itemFilterActive={false}
        />
      </div>

      {/* CTA */}
      <div style={{ padding: "0 14px 10px" }}>
        {wishlistUrl && (
          <a href={wishlistUrl} target="_blank" rel="noopener noreferrer" style={{
            display: "block", textAlign: "center",
            fontFamily: FREDOKA, fontSize: 14, color: "#fff",
            background: "#3b82f6", borderRadius: 12, padding: 11,
            boxShadow: "0 4px 0 #1d4ed8", textDecoration: "none",
          }}>
            🎁 Shop List →
          </a>
        )}
      </div>

      {/* Footer */}
      <div style={{
        borderTop: "2px dashed #e2e8f0", padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ fontFamily: FREDOKA, fontSize: 13, color: "#3b82f6" }}>clearthelist.org</div>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8" }}>#ClearTheList 🍎</div>
      </div>

    </div>
  );
}

const FREDOKA = "'Fredoka One', cursive";
const NUNITO  = "'Nunito', sans-serif";